import { useEffect, useRef } from 'react'
import { ApiError } from '../api/client'
import { processImageUploadQueue } from '../lib/imageSync'
import { Sentry } from '../lib/sentry'
import { syncToServer, syncFromServer, retryFailedOps } from '../lib/sync'
import { useAuthStore } from '../store/auth'

// ── Push intervals ───────────────────────────────────────────────────────
/** Push interval when the tab is active and visible (ms). */
const ACTIVE_PUSH_INTERVAL = 20_000 // 20 seconds

/** Push interval when the tab is hidden / in background (ms). */
const BACKGROUND_PUSH_INTERVAL = 3 * 60_000 // 3 minutes

// ── Pull intervals (adaptive) ────────────────────────────────────────────
/** Minimum pull interval — used right after a pull returns changes (ms). */
const MIN_PULL_INTERVAL_ACTIVE = 20_000 // 20 seconds
const MIN_PULL_INTERVAL_BACKGROUND = 3 * 60_000 // 3 minutes

/** Maximum pull interval — ceiling for the adaptive cooldown (ms). */
const MAX_PULL_INTERVAL_ACTIVE = 2 * 60_000 // 2 minutes
const MAX_PULL_INTERVAL_BACKGROUND = 10 * 60_000 // 10 minutes

// ── Shared constants ─────────────────────────────────────────────────────
/** Base delay for exponential back-off on sync errors (ms). */
const BASE_BACKOFF = 30_000 // 30 seconds

/** Maximum back-off delay (ms). */
const MAX_BACKOFF = 2 * 60_000 // 2 minutes

/**
 * A 401 is not reported: the API client already force-logs-out on it
 * (api/client.ts), which ends sync. Reporting it as well sent one event per
 * pull from any tab whose logout could not run.
 */
const isUnauthorized = (err: unknown) => err instanceof ApiError && err.status === 401

/** Minimum time between syncs to prevent double-firing (ms). */
const SYNC_DEBOUNCE = 2_000 // 2 seconds

/**
 * Smart sync hook that decouples push from pull and adapts pull frequency
 * based on whether the server has changes.
 *
 * **Push** — runs on every tick (cheap: no-op when queue is empty).
 * **Pull** — uses an adaptive cooldown that doubles when no changes are
 * received, and resets to the minimum when changes arrive.
 *
 * Strategy:
 * 1. **Active tab** — push every 20 s, pull every 20 s → 40 s → … → 2 min
 * 2. **Background tab** — push every 3 min, pull every 3 min → … → 10 min
 * 3. **Window focus** — immediate push + pull (catches cross-device edits)
 * 4. **Network reconnect** — immediate push + pull, cooldowns reset
 * 5. **Error back-off** — 30 s → 60 s → 120 s on consecutive failures
 */
export function useSync() {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const consecutiveFailures = useRef(0)
  const lastPushTimestamp = useRef(0)
  const lastPullTimestamp = useRef(0)
  const pullCooldown = useRef(MIN_PULL_INTERVAL_ACTIVE)

  useEffect(() => {
    // ── helpers ────────────────────────────────────────────────────────

    const isPushDebounced = () =>
      Date.now() - lastPushTimestamp.current < SYNC_DEBOUNCE

    const isPullDebounced = () =>
      Date.now() - lastPullTimestamp.current < SYNC_DEBOUNCE

    const shouldPull = () =>
      Date.now() - lastPullTimestamp.current >= pullCooldown.current

    const push = async () => {
      if (!navigator.onLine) return
      if (!useAuthStore.getState().user) return
      if (isPushDebounced()) return

      lastPushTimestamp.current = Date.now()

      try {
        await syncToServer()
        // Process any queued image uploads after successful push
        await processImageUploadQueue()
      } catch (err) {
        if (!isUnauthorized(err)) Sentry.captureException(err)
        console.warn('[Caliu] Push failed', err)
      }
    }

    const pull = async () => {
      if (!navigator.onLine) return
      if (!useAuthStore.getState().user) return
      if (isPullDebounced()) return

      lastPullTimestamp.current = Date.now()

      try {
        const { hasChanges } = await syncFromServer()

        // Adapt pull cooldown based on whether we got changes
        const minInterval = document.hidden
          ? MIN_PULL_INTERVAL_BACKGROUND
          : MIN_PULL_INTERVAL_ACTIVE
        const maxInterval = document.hidden
          ? MAX_PULL_INTERVAL_BACKGROUND
          : MAX_PULL_INTERVAL_ACTIVE

        if (hasChanges) {
          // Changes found — reset to minimum (stay responsive)
          pullCooldown.current = minInterval
        } else {
          // No changes — double the cooldown (up to max)
          pullCooldown.current = Math.min(pullCooldown.current * 2, maxInterval)
        }

        consecutiveFailures.current = 0
      } catch (err) {
        consecutiveFailures.current += 1
        if (!isUnauthorized(err)) Sentry.captureException(err)
        console.warn(
          `[Caliu] Pull failed (attempt ${consecutiveFailures.current})`,
          err,
        )
      }
    }

    /** Return the effective tick interval, accounting for back-off. */
    const getTickInterval = () => {
      const base = document.hidden
        ? BACKGROUND_PUSH_INTERVAL
        : ACTIVE_PUSH_INTERVAL

      if (consecutiveFailures.current === 0) return base

      // Exponential back-off: base_backoff * 2^(failures-1), capped
      const backoff = Math.min(
        BASE_BACKOFF * 2 ** (consecutiveFailures.current - 1),
        MAX_BACKOFF,
      )
      return Math.max(base, backoff)
    }

    /** Schedule the next sync tick using setTimeout (recursive). */
    const scheduleNext = () => {
      stopTimeout()
      timeoutRef.current = setTimeout(async () => {
        await push()
        if (shouldPull()) {
          await pull()
        }
        scheduleNext() // re-schedule with fresh interval
      }, getTickInterval())
    }

    const stopTimeout = () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }

    // ── event handlers ────────────────────────────────────────────────

    /** Tab visibility changed — adjust polling frequency. */
    const handleVisibility = () => {
      if (document.hidden) {
        console.debug('[Caliu] Tab hidden — reducing sync frequency')
        // Reset pull cooldown to background minimum
        pullCooldown.current = MIN_PULL_INTERVAL_BACKGROUND
        scheduleNext()
      } else {
        console.debug('[Caliu] Tab visible — syncing & increasing frequency')
        // Reset pull cooldown to active minimum for immediate responsiveness
        pullCooldown.current = MIN_PULL_INTERVAL_ACTIVE
        push()
        pull()
        scheduleNext()
      }
    }

    /** Window regained focus — immediate push + pull. */
    const handleFocus = () => {
      console.debug('[Caliu] Window focused — syncing')
      push()  // debounce prevents double-sync with visibilitychange
      pull()
      scheduleNext()
    }

    /** Network came back online — flush pending ops + sync. */
    const handleOnline = () => {
      console.debug('[Caliu] Back online — syncing pending operations…')
      consecutiveFailures.current = 0
      lastPushTimestamp.current = 0 // bypass debounce for reconnect
      lastPullTimestamp.current = 0
      pullCooldown.current = MIN_PULL_INTERVAL_ACTIVE
      retryFailedOps()
      // Never drain the upload queue without a user: in local mode every
      // attempt 401s and burns the 3-retry budget, after which the failed-row
      // sweep purges the bytes forever. The queue drains on sign-in.
      if (useAuthStore.getState().user) void processImageUploadQueue()
      push()
      pull()
      scheduleNext()
    }

    // ── bootstrap ─────────────────────────────────────────────────────

    // Initial sync on mount
    if (navigator.onLine && useAuthStore.getState().user) {
      retryFailedOps()
      push()
      pull()
    }

    scheduleNext()

    // Register listeners
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('online', handleOnline)

    return () => {
      stopTimeout()
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('online', handleOnline)
    }
  }, [])
}
